import React from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Button, Input, Label, Table, Th, Td, Tr } from '@/shared/ui'
import { listEarnedCommissionsForAffiliate } from '../services/affiliatePayoutService'

type Props = {
  affiliateUserId: string
  open: boolean
  onClose: () => void
  onConfirm: (commissionIds: string[], from?: string | null, to?: string | null) => Promise<void>
}

export default function GeneratePayoutModal({
  affiliateUserId,
  open,
  onClose,
  onConfirm,
}: Props) {
  const [from, setFrom] = React.useState<string>('')
  const [to, setTo] = React.useState<string>('')
  const [items, setItems] = React.useState<any[]>([])
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setItems([])
      setSelected(new Set())
      setError(null)
      setFrom('')
      setTo('')
    } else {
      // Ao abrir o modal, carrega automaticamente as comiss\u00f5es maturadas dispon\u00edveis
      ;(async () => {
        try {
          setLoading(true)
          setError(null)
          const list = await listEarnedCommissionsForAffiliate(affiliateUserId, null, null)
          setItems(list)
          setSelected(new Set(list.map((x) => x.id)))
        } catch (e: any) {
          setError(e?.message || 'Falha ao carregar comiss\u00f5es')
        } finally {
          setLoading(false)
        }
      })()
    }
  }, [open, affiliateUserId])

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const list = await listEarnedCommissionsForAffiliate(affiliateUserId, from || null, to || null)
      setItems(list)
      setSelected(new Set(list.map((x) => x.id)))
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar comiss\u00f5es')
    } finally {
      setLoading(false)
    }
  }

  async function submit() {
    try {
      await onConfirm(Array.from(selected), from || null, to || null)
      onClose()
    } catch (e: any) {
      setError(e?.message || 'Falha ao gerar payout')
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose()
      }}
      className="p-6"
    >
      <div className="mb-4 text-lg font-semibold">Gerar Payout</div>
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>De (matura\u00e7\u00e3o)</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>At\u00e9 (matura\u00e7\u00e3o)</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" onClick={load} disabled={loading}>
          Carregar
        </Button>
        <Button
          variant="outline"
          onClick={() => setSelected(new Set(items.map((x) => x.id)))}
          disabled={items.length === 0}
        >
          Selecionar tudo
        </Button>
        <Button
          variant="outline"
          onClick={() => setSelected(new Set())}
          disabled={items.length === 0}
        >
          Limpar
        </Button>
      </div>
      <div className="mt-4 max-h-72 overflow-auto rounded-md border border-border/60">
        <Table>
          <thead>
            <Tr>
              <Th className="w-8" />
              <Th>Maturada em</Th>
              <Th>Valor</Th>
            </Tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <Tr key={c.id}>
                <Td>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={(e) => {
                      const next = new Set(selected)
                      if (e.target.checked) next.add(c.id)
                      else next.delete(c.id)
                      setSelected(next)
                    }}
                  />
                </Td>
                <Td>{c.matured_at ? new Date(c.matured_at).toLocaleString() : '\u2014'}</Td>
                <Td>R$ {(Number(c.amount_cents || 0) / 100).toFixed(2)}</Td>
              </Tr>
            ))}
            {items.length === 0 && (
              <Tr>
                <Td colSpan={3} className="py-6 text-center text-sm text-foreground/70">
                  {'Nenhuma comiss\u00e3o.'}
                </Td>
              </Tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={submit} disabled={selected.size === 0}>
          Gerar Payout
        </Button>
      </div>
    </Modal>
  )
}

